// packExport.js -- Lane 789. The two downloads, and the browser plumbing they
// share.
//
// WHY THIS FILE EXISTS. The redesign consolidates every action into ONE toolbar,
// which means the prep-pack download now has TWO callers: the toolbar's download
// menu (where the buyer picks which of the two artifacts they want) and
// SlideBriefing's own button inside the full-plan sheet. A second copy of the
// fetch would be a second place for the endpoint, the filename or the
// internal-only contract to drift, so there is one.
//
// ⚠️ THE TWO FILES ARE NOT THE SAME FILE, AND THIS MODULE DOES NOT BLUR THEM.
//   * THE DECK      -- POST /api/decks/compositions/{id}/export. Supplier-facing.
//     It goes through DeckAssembler's own gated `download()` (the Lane-72 §18
//     internal-slides warn-and-confirm), which is why it is NOT here: routing it
//     through a shared "download something" helper is exactly how that guardrail
//     would one day get bypassed.
//   * THE PREP PACK -- POST .../briefing/export. Internal only. Never folded
//     into the deck export, and the server composes it. That is this file.
// Only the blob/anchor plumbing is genuinely common, and that is what is shared.

/**
 * Hand a finished HTML string to the browser as a download.
 * Pure DOM plumbing -- no fetch, no policy, no knowledge of which artifact it is.
 */
export function triggerHtmlDownload(html, filename) {
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Download the INTERNAL meeting-prep pack for one saved composition.
 *
 * The export path is deliberately read-only server-side (Lane 738): downloading
 * never starts a paid enrichment pass. It therefore reads whatever the pack
 * currently is -- which is why both callers hold the control while a pass is
 * still running (Lane 768): a page fills itself in later, a file does not.
 *
 * @throws {Error} on any non-OK response -- the caller is a buyer who just
 *   clicked and is watching, so a silent failure is not an option.
 */
export async function downloadPackHtml({ compositionId, apiBase, authHeaders, isCurrent = () => true, native = false }) {
  const r = await fetch(
    `${apiBase}/api/decks/compositions/${compositionId}/${native ? "prep-pack/export" : "briefing/export"}`,
    { method: native ? 'GET' : 'POST', headers: authHeaders() },
  );
  if (!r.ok) throw new Error(`Download failed (${r.status})`);
  const html = await r.text();
  if (isCurrent()) triggerHtmlDownload(html, `meeting-plan-${compositionId}.html`);
}
